import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { isEmailVerifiedClaim } from "../../utils/auth";

/**
 * Intuit App Store / QBO "Apps" tab Disconnect URL.
 *
 * Intuit has already revoked the company's OAuth tokens on their side before
 * sending the user's browser here, optionally appending the disconnected company's
 * `realmId` as a query param. Per Intuit's SSO requirements this page must
 * perform an OpenID auth request (using connection: "quickbooks-online"),
 * then land the user directly inside the application with the OAuth connection
 * terminated and the "Connect to QuickBooks" widget visible.
 */
const IntuitDisconnect = () => {
  const { loginWithRedirect, isAuthenticated, isLoading, user, getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const hasStarted = useRef(false);

  useEffect(() => {
    if (isLoading || hasStarted.current) return;
    hasStarted.current = true;

    const realmId = new URLSearchParams(window.location.search).get("realmId") || "";
    const returnTo = realmId
      ? `/intuit-disconnect-complete?realmId=${encodeURIComponent(realmId)}`
      : "/intuit-disconnect-complete";

    if (isAuthenticated) {
      (async () => {
        const claims = await getIdTokenClaims().catch(() => null);
        if (isEmailVerifiedClaim(claims, user) === false) {
          navigate("/intuit-unverified", { replace: true });
          return;
        }
        navigate(returnTo, { replace: true });
      })();
      return;
    }

    loginWithRedirect({
      authorizationParams: {
        connection: "quickbooks-online",
        scope: "openid profile email",
      },
      appState: {
        disconnectFlow: true,
        disconnectRealmId: realmId,
      },
    });
  }, [isLoading, isAuthenticated, loginWithRedirect, navigate, user, getIdTokenClaims]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <Spin size="large" />
    </div>
  );
};

export default IntuitDisconnect;
